import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingSomersetWestPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Somerset West Professional Painters - Interior & Exterior Painting | Roof Coating Services',
    description: 'Expert painting services in Somerset West, Strand, and Gordons Bay. Specialized in interior, exterior, and roof coating with premium materials. Over 15 years of trusted service in the Helderberg area.',
    keywords: 'painters Somerset West, house painters Somerset West, interior painting, exterior painting, roof coating, residential painting, commercial painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Somerset West',
          description: 'Professional painting services in Somerset West and Helderberg',
          address: 'Somerset West, Cape Town',
          priceRange: '$$',
          serviceArea: ['Somerset West', 'Strand', 'Gordons Bay', 'Helderberg', 'Sir Lowrys Pass']
        }),
        generateServiceSchema({
          name: 'Interior, Exterior & Roof Services',
          description: 'Complete painting and coating solutions',
          provider: 'Painters Somerset West',
          areaServed: ['Somerset West', 'Strand', 'Gordons Bay'],
          serviceType: ['Interior Painting', 'Exterior Painting', 'Roof Coating']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Somerset West",
    location: "Somerset West, Western Cape",
    website: "https://painterssomersetwest.co.za/",
    heroImage: "https://public.readdy.ai/ai/img_res/ead9b37e6d36eaffb8fd0272d3861f65.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20somerset%20west%20home%20exterior%20with%20mountain%20backdrop%20modern%20architecture%20white%20walls%20professional%20finish%20clear%20blue%20sky&width=800&height=600&seq=gallery-sw1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20room%20painting%20somerset%20west%20style%20sophisticated%20neutral%20colors%20modern%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=gallery-sw2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=exterior%20house%20painting%20project%20somerset%20west%20residential%20property%20professional%20painters%20working%20quality%20finish%20suburban%20setting&width=800&height=600&seq=gallery-sw3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20coating%20application%20somerset%20west%20property%20professional%20work%20weather%20resistant%20treatment%20modern%20residential%20building%20mountain%20views&width=800&height=600&seq=gallery-sw4&orientation=landscape"
    ],
    services: [
      {
        title: "Interior Painting",
        description: "Transform your home's interior with our expert painting services. We create beautiful, durable finishes that reflect your style and enhance your living spaces throughout Somerset West and surrounding areas.",
        features: ["All interior rooms", "Feature walls & accents", "Ceiling painting", "Woodwork & trim", "Kitchen & bathroom painting", "Professional color consultation"]
      },
      {
        title: "Exterior Painting",
        description: "Protect and beautify your property's exterior with our professional painting services. We use premium weather-resistant paints designed to withstand the Helderberg region's climate conditions.",
        features: ["House facades", "Exterior walls & cladding", "Window frames & doors", "Fascias & soffits", "Garden walls & fences", "Weather protection coatings"]
      },
      {
        title: "Roof Coating",
        description: "Extend your roof's lifespan and improve your home's energy efficiency with our specialized roof coating services. Essential protection for Somerset West properties.",
        features: ["Tile roof painting", "Metal roof coating", "Waterproofing systems", "Heat-reflective coatings", "Leak repair & prevention", "Roof cleaning & preparation"]
      }
    ],
    serviceAreas: ["Somerset West", "Strand", "Gordons Bay", "Macassar", "Sir Lowry's Pass Village", "Firgrove", "Lwandle", "Nomadolo"],
    about: "Painters Somerset West is a trusted painting company serving Somerset West and the greater Helderberg region. With over 13 years of experience in the painting industry, we have established ourselves as one of the most reliable and professional painting contractors in the area, completing hundreds of successful projects for satisfied homeowners and businesses.",
    aboutExtended: [
      "Our company specializes in both residential and commercial painting projects throughout Somerset West, Strand, Gordons Bay, and surrounding suburbs. We understand the unique characteristics of properties in the Helderberg region and the specific challenges posed by the local climate. Our expertise covers everything from modern estates to older homes, from single-room refreshes to complete property transformations.",
      "We are committed to using only premium quality paints and materials from trusted brands, ensuring long-lasting results that stand up to the region's weather conditions. Our team of experienced painters is fully trained, certified, and insured, providing you with complete confidence and peace of mind throughout your project. We take pride in our attention to detail, professional work ethic, and commitment to customer satisfaction.",
      "What makes us different is our personalized approach to every project. We take the time to understand your specific needs, preferences, and budget, providing expert advice and guidance throughout the process. From initial color consultation to final inspection, we maintain clear communication and deliver results that exceed expectations. Our comprehensive service includes thorough surface preparation, expert application, and meticulous cleanup."
    ],
    ourApproach: [
      "Free on-site consultation and assessment",
      "Detailed project planning and quotation",
      "Comprehensive surface preparation",
      "Premium quality paints and materials",
      "Professional application techniques",
      "Quality control at every stage",
      "Clean and organized work practices",
      "Final inspection and walkthrough",
      "Customer satisfaction guarantee"
    ],
    whyChoose: [
      "13+ years serving Somerset West",
      "Hundreds of satisfied customers",
      "Fully licensed and insured",
      "Experienced professional team",
      "Premium quality materials",
      "Competitive pricing",
      "Free detailed quotes",
      "Comprehensive warranties",
      "Excellent local reputation",
      "Personalized service",
      "Attention to detail",
      "Reliable and punctual"
    ],
    testimonials: [
      {
        name: "Susan Marais",
        rating: 5,
        text: "Fantastic service! The team was professional, friendly, and did an excellent job on our home. The attention to detail was impressive and the results are beautiful. Highly recommend!"
      },
      {
        name: "Peter Daniels",
        rating: 5,
        text: "Very pleased with the quality of work. They were punctual, efficient, and cleaned up thoroughly each day. Our house looks amazing! Great value for money."
      },
      {
        name: "Natalie Fourie",
        rating: 5,
        text: "Excellent experience from start to finish. The team was knowledgeable, helpful with color selection, and completed the work on schedule. We couldn't be happier with the results!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Somerset West</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Somerset West</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in the Helderberg region:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Comprehensive painting solutions for homes and businesses throughout Somerset West and the Helderberg region.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Somerset West, Strand, Gordons Bay</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 8:30AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
